/**
 * 我的鱼缸页面
 * 展示用户的鱼缸列表及管理功能
 * 黑白主题风格（Vercel 风格）
 */

import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Container,
  Plus,
  Loader2,
  Edit,
  Trash2,
  Droplets,
  Home,
  Sun,
  Camera,
  X,
  AlertCircle,
  Ruler,
  Settings,
  Clock,
  MoreVertical,
} from "lucide-react";
import { userApiClient } from "@/lib/userApiClient";
import { UnitToggle } from "@/components/tanks/UnitToggle";
import { TurnoverRateDisplay } from "@/components/tanks/TurnoverRateDisplay";
import {
  WaterTypeBadge,
  type WaterType,
} from "@/components/tanks/WaterTypeBadge";
import {
  useVolumeCalculator,
  getUnitPreference,
  setUnitPreference,
  type UnitSystem,
} from "@/hooks/useVolumeCalculator";
import {
  cmToInches,
  inchesToCm,
  lphToGph,
  gphToLph,
} from "@/lib/tankCalculations";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import SubsectionHeader from "@/components/shared/SubsectionHeader";

interface TankData {
  id: number;
  name: string;
  length: number;
  width: number;
  height: number;
  actual_water_level: number | null;
  volume: number;
  effective_volume?: number;
  dimensions: string;
  filter_system: string;
  filter_system_text: string;
  location: string;
  location_text: string;
  pump_flow_rate: number | null;
  active: boolean;
  created_at: string;
  cover_image_url: string | null;
  water_type: WaterType;
  water_type_text: string;
}

interface TankFormData {
  name: string;
  length: string;
  width: string;
  height: string;
  actual_water_level: string;
  filter_system: string;
  location: string;
  pump_flow_rate: string;
  water_type: WaterType;
}

interface SelectOption {
  value: string;
  label: string;
}

// TankCard 组件的 Props
interface TankCardProps {
  tank: TankData;
  unitSystem: UnitSystem;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: any;
  onEdit: (tank: TankData) => void;
  onDelete: (tank: TankData) => void;
}

/**
 * 鱼缸卡片组件 - 展示单个鱼缸信息
 * 优化版本：更清晰的视觉层次、更好的交互体验
 */
function TankCard({ tank, unitSystem, t, onEdit, onDelete }: TankCardProps) {
  // 计算有效水量和循环倍率
  const volumeResult = useVolumeCalculator({
    length: unitSystem === "metric" ? tank.length : cmToInches(tank.length),
    width: unitSystem === "metric" ? tank.width : cmToInches(tank.width),
    height: unitSystem === "metric" ? tank.height : cmToInches(tank.height),
    actualWaterLevel: tank.actual_water_level
      ? unitSystem === "metric"
        ? tank.actual_water_level
        : cmToInches(tank.actual_water_level)
      : null,
    pumpFlowRate: tank.pump_flow_rate
      ? unitSystem === "metric"
        ? tank.pump_flow_rate
        : lphToGph(tank.pump_flow_rate)
      : null,
    unitSystem,
  });

  // 单位标签
  const volumeUnit =
    unitSystem === "metric" ? t("units.literAbbr") : t("units.gallonAbbr");
  const flowRateUnit =
    unitSystem === "metric"
      ? t("units.litersPerHourAbbr")
      : t("units.gallonsPerHourAbbr");
  const dimensionUnit =
    unitSystem === "metric" ? t("units.centimeterAbbr") : t("units.inchAbbr");

  // 格式化尺寸显示
  const formatDimensions = (): string => {
    if (unitSystem === "metric") {
      return `${tank.length}×${tank.width}×${tank.height}`;
    }
    const l = cmToInches(tank.length).toFixed(0);
    const w = cmToInches(tank.width).toFixed(0);
    const h = cmToInches(tank.height).toFixed(0);
    return `${l}×${w}×${h}`;
  };

  // 格式化流量显示
  const formatFlowRate = (): string | null => {
    if (!tank.pump_flow_rate) return null;
    if (unitSystem === "metric") {
      return `${tank.pump_flow_rate}`;
    }
    return `${lphToGph(tank.pump_flow_rate).toFixed(0)}`;
  };

  // 循环倍率状态颜色
  const getTurnoverStatus = () => {
    if (!volumeResult.turnoverStatus) return null;
    const config = {
      low: {
        color: "text-amber-500 bg-amber-50 dark:bg-amber-950/30",
        label: "偏低",
      },
      normal: {
        color: "text-emerald-500 bg-emerald-50 dark:bg-emerald-950/30",
        label: "适中",
      },
      high: {
        color: "text-blue-500 bg-blue-50 dark:bg-blue-950/30",
        label: "充沛",
      },
    };
    return config[volumeResult.turnoverStatus as keyof typeof config] || null;
  };
  const turnoverStatus = getTurnoverStatus();

  // 水体类型颜色配置
  const waterTypeColors: Record<
    WaterType,
    { bg: string; border: string; icon: string }
  > = {
    freshwater: {
      bg: "from-emerald-500 via-teal-500 to-cyan-500",
      border: "border-emerald-200 dark:border-emerald-800",
      icon: "🌿",
    },
    saltwater: {
      bg: "from-blue-500 via-indigo-500 to-purple-500",
      border: "border-blue-200 dark:border-blue-800",
      icon: "🪸",
    },
    brackish: {
      bg: "from-amber-500 via-orange-500 to-yellow-500",
      border: "border-amber-200 dark:border-amber-800",
      icon: "🌊",
    },
  };
  const waterTypeColor =
    waterTypeColors[tank.water_type as WaterType] || waterTypeColors.freshwater;

  // 格式化创建时间
  const formatCreatedAt = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24),
    );
    if (diffDays === 0) return "今天";
    if (diffDays === 1) return "昨天";
    if (diffDays < 7) return `${diffDays}天前`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}周前`;
    return date.toLocaleDateString("zh-CN");
  };

  return (
    <Card className="group overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border-muted">
      <CardContent className="p-0">
        {/* 顶部区域：封面图片 + 渐变遮罩 */}
        <div className="relative aspect-[16/9] overflow-hidden">
          {tank.cover_image_url ? (
            <>
              <img
                src={tank.cover_image_url}
                alt={tank.name}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              {/* 渐变遮罩 */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              {/* 水体类型徽章 */}
              <div className="absolute top-3 left-3">
                <WaterTypeBadge
                  waterType={(tank.water_type || "freshwater") as WaterType}
                  variant="solid"
                />
              </div>
            </>
          ) : (
            <>
              {/* 无图片时的渐变背景 */}
              <div
                className={`w-full h-full bg-gradient-to-br ${waterTypeColor.bg} flex flex-col items-center justify-center text-white relative`}
              >
                {/* 水波纹装饰 */}
                <div className="absolute inset-0 opacity-20">
                  <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-[linear-gradient(0deg,rgba(255,255,255,0.1)_0%,transparent_100%)]" />
                </div>
                <Droplets className="h-8 w-8 sm:h-10 sm:w-10 mb-2 opacity-90" />
                <span className="text-4xl sm:text-5xl font-bold">
                  {Math.round(volumeResult.effectiveVolume)}
                </span>
                <span className="text-lg font-medium opacity-90">
                  {volumeUnit}
                </span>
              </div>
              {/* 水体类型标签 */}
              <div className="absolute top-3 left-3">
                <WaterTypeBadge
                  waterType={(tank.water_type || "freshwater") as WaterType}
                  variant="solid"
                />
              </div>
            </>
          )}

          {/* 右上角操作菜单 */}
          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button
              variant="secondary"
              size="icon"
              className="h-8 w-8 rounded-full shadow-md bg-slate-900/85 hover:bg-slate-900 text-white border border-white/20"
              onClick={() => onEdit(tank)}
            >
              <Edit className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {/* 主体内容区 */}
        <div className="p-4">
          {/* 标题行 */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-semibold text-foreground truncate">
                {tank.name}
              </h3>
              <div className="flex items-center gap-1.5 mt-1 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" />
                <span>{formatCreatedAt(tank.created_at)}创建</span>
              </div>
            </div>
          </div>

          {/* 核心数据条 */}
          <div className="flex items-center justify-between p-2.5 rounded-lg bg-muted/50 mb-3">
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30">
                <Droplets className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <div className="text-sm font-semibold text-foreground">
                  {Math.round(volumeResult.effectiveVolume)} {volumeUnit}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  有效水量
                </div>
              </div>
            </div>

            {/* 循环倍率指示器 */}
            {turnoverStatus && (
              <div
                className={`flex items-center gap-1.5 px-2 py-1 rounded-full ${turnoverStatus.color}`}
              >
                <span className="text-sm font-bold">
                  {volumeResult.turnoverRate?.toFixed(1)}×
                </span>
                <span className="text-[10px] font-medium">
                  {turnoverStatus.label}
                </span>
              </div>
            )}
          </div>

          {/* 详细信息网格 */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            {/* 尺寸 */}
            <div className="flex items-center gap-2 p-2 rounded-md bg-muted/30">
              <Ruler className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-xs text-muted-foreground">尺寸</div>
                <div className="text-sm font-medium truncate">
                  {formatDimensions()} {dimensionUnit}
                </div>
              </div>
            </div>

            {/* 过滤系统 */}
            <div className="flex items-center gap-2 p-2 rounded-md bg-muted/30">
              <Settings className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-xs text-muted-foreground">过滤</div>
                <div className="text-sm font-medium truncate">
                  {(t as any)(`filterSystems.${tank.filter_system}`)}
                </div>
              </div>
            </div>

            {/* 位置 */}
            <div className="flex items-center gap-2 p-2 rounded-md bg-muted/30">
              {tank.location === "indoor" ? (
                <Home className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              ) : (
                <Sun className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              )}
              <div className="min-w-0">
                <div className="text-xs text-muted-foreground">位置</div>
                <div className="text-sm font-medium truncate">
                  {(t as any)(`locations.${tank.location}`)}
                </div>
              </div>
            </div>

            {/* 流量 (如果有) */}
            {tank.pump_flow_rate ? (
              <div className="flex items-center gap-2 p-2 rounded-md bg-muted/30">
                <Droplets className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-xs text-muted-foreground">流量</div>
                  <div className="text-sm font-medium truncate">
                    {formatFlowRate()} {flowRateUnit}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 p-2 rounded-md bg-muted/30 opacity-50">
                <Droplets className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-xs text-muted-foreground">流量</div>
                  <div className="text-sm font-medium">--</div>
                </div>
              </div>
            )}
          </div>

          {/* 操作按钮 */}
          <div className="flex gap-2 pt-3 border-t">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 h-9"
              onClick={() => onEdit(tank)}
            >
              <Edit className="h-4 w-4 mr-1.5" />
              编辑
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-destructive hover:text-destructive hover:bg-destructive/10 h-9 px-3"
              onClick={() => onDelete(tank)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * 我的鱼缸 - 展示用户的鱼缸列表
 */
export default function Tanks() {
  const { t } = useTranslation("tanks");
  const [tankList, setTankList] = useState<TankData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingTank, setEditingTank] = useState<TankData | null>(null);
  const [deletingTank, setDeletingTank] = useState<TankData | null>(null);
  const [unitSystem, setUnitSystem] = useState<UnitSystem>(() =>
    getUnitPreference(),
  );

  // 使用前端 i18n 翻译生成选项
  const FILTER_SYSTEM_KEYS = [
    "no_filter",
    "external_filter",
    "waterfall_filter",
    "internal_filter",
    "canister_filter",
    "sump_filter",
    "built_in_filter",
    "air_filter",
    "sump_box_filter",
    "undergravel_filter",
  ];

  const LOCATION_KEYS = ["indoor", "outdoor"];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const filterSystemOptions: SelectOption[] = FILTER_SYSTEM_KEYS.map((key) => ({
    value: key,
    label: (t as any)(`filterSystems.${key}`),
  }));

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const locationOptions: SelectOption[] = LOCATION_KEYS.map((key) => ({
    value: key,
    label: (t as any)(`locations.${key}`),
  }));

  const [formData, setFormData] = useState<TankFormData>({
    name: "",
    length: "",
    width: "",
    height: "",
    actual_water_level: "",
    filter_system: "",
    location: "indoor",
    pump_flow_rate: "",
    water_type: "freshwater",
  });

  // 封面图片状态
  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [coverImagePreview, setCoverImagePreview] = useState<string | null>(
    null,
  );

  // 表单验证错误
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // 加载鱼缸列表
  const loadTankList = async () => {
    setIsLoading(true);
    try {
      const data = await userApiClient.get<{ tanks: TankData[] }>("/tanks");
      setTankList(data.tanks || []);
    } catch (error) {
      console.error("加载鱼缸列表失败:", error);
      toast.error(t("messages.loadFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  // 页面加载时获取数据
  useEffect(() => {
    loadTankList();
  }, []);

  // 单位切换处理 - 同时转换已填写的表单数据
  const handleUnitChange = (newUnit: UnitSystem) => {
    if (newUnit === unitSystem) return;

    // 转换已填写的尺寸和流量数据
    const convertValue = (value: string, isFlowRate = false): string => {
      if (!value || value === "") return "";
      const num = parseFloat(value);
      if (isNaN(num)) return "";

      if (newUnit === "imperial") {
        // 公制 -> 英制
        return isFlowRate
          ? lphToGph(num).toFixed(1)
          : cmToInches(num).toFixed(2);
      } else {
        // 英制 -> 公制
        return isFlowRate
          ? gphToLph(num).toFixed(1)
          : inchesToCm(num).toFixed(2);
      }
    };

    setFormData((prev) => ({
      ...prev,
      length: convertValue(prev.length),
      width: convertValue(prev.width),
      height: convertValue(prev.height),
      actual_water_level: convertValue(prev.actual_water_level),
      pump_flow_rate: convertValue(prev.pump_flow_rate, true),
    }));

    setUnitPreference(newUnit);
    setUnitSystem(newUnit);
  };

  // 将公制值转换为当前单位系统的显示值
  const convertToDisplayUnit = (
    valueCm: number,
    isFlowRate = false,
  ): string => {
    if (unitSystem === "metric") {
      return valueCm.toString();
    }
    if (isFlowRate) {
      return lphToGph(valueCm).toFixed(1);
    }
    return cmToInches(valueCm).toFixed(2);
  };

  // 将当前单位系统的输入值转换为公制存储值
  const convertToMetric = (value: string, isFlowRate = false): number => {
    const num = parseFloat(value) || 0;
    if (unitSystem === "metric") {
      return num;
    }
    if (isFlowRate) {
      return gphToLph(num);
    }
    return inchesToCm(num);
  };

  // 打开添加对话框
  const handleAddTank = () => {
    setEditingTank(null);
    setFormData({
      name: "",
      length: "",
      width: "",
      height: "",
      actual_water_level: "",
      filter_system: filterSystemOptions[0]?.value || "",
      location: "indoor",
      pump_flow_rate: "",
      water_type: "freshwater",
    });
    setCoverImage(null);
    setCoverImagePreview(null);
    setFormErrors({});
    setIsDialogOpen(true);
  };

  // 打开编辑对话框
  const handleEditTank = (tank: TankData) => {
    setEditingTank(tank);
    setFormData({
      name: tank.name,
      length: convertToDisplayUnit(tank.length),
      width: convertToDisplayUnit(tank.width),
      height: convertToDisplayUnit(tank.height),
      actual_water_level: tank.actual_water_level
        ? convertToDisplayUnit(tank.actual_water_level)
        : "",
      filter_system: tank.filter_system,
      location: tank.location,
      pump_flow_rate: tank.pump_flow_rate
        ? convertToDisplayUnit(tank.pump_flow_rate, true)
        : "",
      water_type: tank.water_type || "freshwater",
    });
    setCoverImage(null);
    setCoverImagePreview(tank.cover_image_url);
    setFormErrors({});
    setIsDialogOpen(true);
  };

  // 验证表单
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    const maxDimension = unitSystem === "metric" ? 999 : 393; // 999cm ≈ 393in

    // 验证必填字段
    if (!formData.name.trim()) {
      errors.name = t("validation.nameRequired");
    }

    // 验证尺寸
    const length = parseFloat(formData.length) || 0;
    const width = parseFloat(formData.width) || 0;
    const height = parseFloat(formData.height) || 0;

    if (!formData.length || !formData.width || !formData.height) {
      errors.dimensions = t("validation.dimensionRequired");
    } else if (length <= 0 || width <= 0 || height <= 0) {
      errors.dimensions = t("validation.dimensionPositive");
    } else if (
      length > maxDimension ||
      width > maxDimension ||
      height > maxDimension
    ) {
      errors.dimensions = t("validation.dimensionMax", {
        max: maxDimension,
        unit: unitSystem === "metric" ? "cm" : "in",
      });
    }

    // 验证水位
    const waterLevel = parseFloat(formData.actual_water_level) || 0;
    if (formData.actual_water_level && waterLevel > height) {
      errors.actual_water_level = t("validation.waterLevelExceedsHeight");
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // 处理封面图片选择
  const handleCoverImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // 验证文件类型
      if (!file.type.startsWith("image/")) {
        toast.error(t("validation.invalidImageType"));
        return;
      }
      // 验证文件大小（最大 5MB）
      if (file.size > 5 * 1024 * 1024) {
        toast.error(t("validation.imageTooLarge"));
        return;
      }
      setCoverImage(file);
      setCoverImagePreview(URL.createObjectURL(file));
    }
  };

  // 移除封面图片
  const handleRemoveCoverImage = () => {
    setCoverImage(null);
    setCoverImagePreview(null);
  };

  // 处理表单提交
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const url = editingTank ? `/tanks/${editingTank.id}` : "/tanks";
      const method = editingTank ? "PATCH" : "POST";

      // 使用 FormData 支持文件上传
      const formDataToSend = new FormData();
      formDataToSend.append("tank[name]", formData.name);
      formDataToSend.append(
        "tank[length]",
        Math.round(convertToMetric(formData.length)).toString(),
      );
      formDataToSend.append(
        "tank[width]",
        Math.round(convertToMetric(formData.width)).toString(),
      );
      formDataToSend.append(
        "tank[height]",
        Math.round(convertToMetric(formData.height)).toString(),
      );
      if (formData.actual_water_level) {
        formDataToSend.append(
          "tank[actual_water_level]",
          Math.round(convertToMetric(formData.actual_water_level)).toString(),
        );
      }
      formDataToSend.append("tank[filter_system]", formData.filter_system);
      formDataToSend.append("tank[location]", formData.location);
      formDataToSend.append("tank[water_type]", formData.water_type);
      if (formData.pump_flow_rate) {
        formDataToSend.append(
          "tank[pump_flow_rate]",
          Math.round(convertToMetric(formData.pump_flow_rate, true)).toString(),
        );
      }
      if (coverImage) {
        formDataToSend.append("tank[cover_image]", coverImage);
      }

      const result = await userApiClient.request<any>(url, {
        method,
        body: formDataToSend,
      });

      toast.success(
        result.message ||
          (editingTank
            ? t("messages.updateSuccess")
            : t("messages.createSuccess")),
      );
      setIsDialogOpen(false);
      await loadTankList();
    } catch (error: any) {
      console.error("操作失败:", error);
      const errorMessage =
        error.details?.name?.[0] ||
        error.details?.base?.[0] ||
        error.message ||
        (editingTank ? t("messages.updateFailed") : t("messages.createFailed"));
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 删除鱼缸
  const handleDeleteTank = async () => {
    if (!deletingTank) return;

    try {
      const result = await userApiClient.delete<any>(
        `/tanks/${deletingTank.id}`,
      );

      toast.success(result.message || t("messages.deleteSuccess"));
      setDeletingTank(null);
      await loadTankList();
    } catch (error) {
      console.error("删除失败:", error);
      toast.error(t("messages.loadFailed"));
    }
  };

  // 更新表单字段
  const updateField = (field: keyof TankFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // 清除相关错误
    if (field === "name" && formErrors.name) {
      setFormErrors((prev) => ({ ...prev, name: "" }));
    }
    if (
      ["length", "width", "height"].includes(field) &&
      formErrors.dimensions
    ) {
      setFormErrors((prev) => ({ ...prev, dimensions: "" }));
    }
    if (field === "actual_water_level" && formErrors.actual_water_level) {
      setFormErrors((prev) => ({ ...prev, actual_water_level: "" }));
    }
  };

  // 使用 Hook 计算容积
  const volumeResult = useVolumeCalculator({
    length: parseFloat(formData.length) || 0,
    width: parseFloat(formData.width) || 0,
    height: parseFloat(formData.height) || 0,
    actualWaterLevel: formData.actual_water_level
      ? parseFloat(formData.actual_water_level)
      : null,
    pumpFlowRate: formData.pump_flow_rate
      ? parseFloat(formData.pump_flow_rate)
      : null,
    unitSystem,
  });

  // 获取单位标签
  const dimensionUnit =
    unitSystem === "metric" ? t("units.centimeterAbbr") : t("units.inchAbbr");
  const volumeUnit =
    unitSystem === "metric" ? t("units.literAbbr") : t("units.gallonAbbr");
  const flowRateUnit =
    unitSystem === "metric"
      ? t("units.litersPerHourAbbr")
      : t("units.gallonsPerHourAbbr");

  return (
    <div className="container mx-auto max-w-4xl px-4 py-4 sm:py-6">
      {/* 页面标题 */}
      <SubsectionHeader
        title={t("title")}
        icon="Container"
        description={t("description")}
        className="mb-4"
      />

      {/* Action Bar - 新建按钮 + 单位切换 */}
      {!isLoading && (
        <div className="flex items-center justify-between mb-4 sm:mb-6 pb-3 sm:pb-4 border-b">
          <Button onClick={handleAddTank} size="sm" className="sm:h-10">
            <Plus className="h-4 w-4 mr-1.5 sm:mr-2" />
            <span className="text-sm sm:text-base">{t("actions.create")}</span>
          </Button>
          <UnitToggle
            value={unitSystem}
            onChange={handleUnitChange}
            metricLabel={t("units.metric")}
            imperialLabel={t("units.imperial")}
          />
        </div>
      )}

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <Skeleton className="aspect-video rounded-lg mb-4" />
                <div className="flex items-center justify-between mb-3">
                  <Skeleton className="h-6 w-32" />
                  <Skeleton className="h-5 w-16 rounded-full" />
                </div>
                <div className="space-y-3">
                  {[1, 2, 3, 4].map((j) => (
                    <div key={j} className="flex justify-between">
                      <Skeleton className="h-4 w-20" />
                      <Skeleton className="h-4 w-24" />
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 pt-4 mt-4 border-t">
                  <Skeleton className="h-8 flex-1 rounded" />
                  <Skeleton className="h-8 w-8 rounded" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : tankList.length === 0 ? (
        <>
          {/* 空状态 - 引导型设计 */}
          <Card className="mb-4 sm:mb-6">
            <CardHeader className="text-center pb-2 px-4 sm:px-6">
              <CardTitle className="text-lg sm:text-xl">
                {t("empty.heroTitle")}
              </CardTitle>
              <CardDescription className="text-sm sm:text-base">
                {t("empty.heroSubtitle")}
              </CardDescription>
            </CardHeader>
            <CardContent className="px-4 sm:px-6">
              <div className="flex flex-col items-center justify-center py-8 sm:py-10 text-center">
                {/* 优化的视觉中心 - 鱼缸图标带淡蓝色背景 */}
                <div className="relative mb-5 sm:mb-6">
                  <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-blue-50 dark:bg-blue-950/30 flex items-center justify-center">
                    <Droplets className="h-10 w-10 sm:h-12 sm:w-12 text-blue-500" />
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary flex items-center justify-center">
                    <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary-foreground" />
                  </div>
                </div>
                {/* 引导箭头和按钮 */}
                <div className="relative">
                  <Button
                    onClick={handleAddTank}
                    size="lg"
                    className="px-6 sm:px-8 text-sm sm:text-base"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {t("empty.addFirstTank")}
                  </Button>
                  {/* 手绘风格引导箭头 - 移动端隐藏 */}
                  <svg
                    className="absolute -right-12 top-1/2 -translate-y-1/2 w-10 h-10 text-muted-foreground/40 hidden sm:block"
                    viewBox="0 0 40 40"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M5 20 Q 15 15, 25 20 T 35 18" />
                    <path d="M30 12 L 35 18 L 28 22" />
                  </svg>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 功能三列 - 核心价值展示 */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            <Card className="border-dashed">
              <CardHeader className="pb-2 px-4 sm:px-6">
                <CardTitle className="text-sm sm:text-base flex items-center gap-2">
                  <span className="text-lg sm:text-xl">📐</span>
                  {t("empty.feature1Title")}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 sm:px-6">
                <p className="text-xs sm:text-sm text-muted-foreground">
                  {t("empty.feature1Desc")}
                </p>
              </CardContent>
            </Card>

            <Card className="border-dashed">
              <CardHeader className="pb-2 px-4 sm:px-6">
                <CardTitle className="text-sm sm:text-base flex items-center gap-2">
                  <span className="text-lg sm:text-xl">⚙️</span>
                  {t("empty.feature2Title")}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 sm:px-6">
                <p className="text-xs sm:text-sm text-muted-foreground">
                  {t("empty.feature2Desc")}
                </p>
              </CardContent>
            </Card>

            <Card className="border-dashed">
              <CardHeader className="pb-2 px-4 sm:px-6">
                <CardTitle className="text-sm sm:text-base flex items-center gap-2">
                  <span className="text-lg sm:text-xl">🧪</span>
                  {t("empty.feature3Title")}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 sm:px-6">
                <p className="text-xs sm:text-sm text-muted-foreground">
                  {t("empty.feature3Desc")}
                </p>
              </CardContent>
            </Card>
          </div>
        </>
      ) : (
        /* 鱼缸列表 */
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          {tankList.map((tank) => (
            <TankCard
              key={tank.id}
              tank={tank}
              unitSystem={unitSystem}
              t={t}
              onEdit={handleEditTank}
              onDelete={setDeletingTank}
            />
          ))}
        </div>
      )}

      {/* 添加/编辑鱼缸对话框 */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[640px] max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b flex-shrink-0">
            <DialogTitle className="flex items-center gap-3 text-lg sm:text-xl font-semibold">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Container className="h-5 w-5 text-primary" />
              </div>
              {editingTank ? t("actions.edit") : t("actions.create")}
            </DialogTitle>
            <DialogDescription className="text-sm">
              {editingTank ? t("dialog.editDescription") : t("description")}
            </DialogDescription>
          </DialogHeader>

          <form
            onSubmit={handleSubmit}
            className="flex flex-col flex-1 min-h-0"
          >
            <div className="flex-1 overflow-y-auto px-6 py-5">
              <div className="space-y-5">
                {/* ========== 基本信息区块 ========== */}
                <div className="space-y-4">
                  {/* 名称 */}
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-sm font-medium">
                      {t("form.name")}{" "}
                      <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => updateField("name", e.target.value)}
                      placeholder={t("form.namePlaceholder")}
                      className={`h-10 ${formErrors.name ? "border-destructive" : ""}`}
                    />
                    {formErrors.name && (
                      <p className="text-sm text-destructive flex items-center gap-1">
                        <AlertCircle className="h-3.5 w-3.5" />
                        {formErrors.name}
                      </p>
                    )}
                  </div>

                  {/* 封面图片 */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      {t("form.coverImage")}
                    </Label>
                    <div className="flex items-center gap-4">
                      {coverImagePreview ? (
                        <div className="relative w-32 h-20 rounded-lg overflow-hidden border-2 flex-shrink-0 group">
                          <img
                            src={coverImagePreview}
                            alt="封面预览"
                            className="w-full h-full object-cover"
                          />
                          <button
                            type="button"
                            onClick={handleRemoveCoverImage}
                            className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                          >
                            <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center">
                              <X className="h-4 w-4 text-gray-900" />
                            </div>
                          </button>
                        </div>
                      ) : (
                        <label className="flex flex-col items-center justify-center w-32 h-20 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50 hover:border-primary/50 transition-all flex-shrink-0">
                          <Camera className="h-6 w-6 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground mt-1">
                            {t("form.uploadCover")}
                          </span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleCoverImageChange}
                            className="hidden"
                          />
                        </label>
                      )}
                      <div className="flex-1">
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          {t("form.coverImageHint")}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ========== 尺寸与容积区块 ========== */}
                <div className="space-y-4 pt-5 border-t">
                  {/* 尺寸标题行：标签 + 单位切换 */}
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">
                      {t("form.dimensions") || "尺寸"} ({dimensionUnit}){" "}
                      <span className="text-destructive">*</span>
                    </Label>
                    <UnitToggle
                      value={unitSystem}
                      onChange={handleUnitChange}
                      metricLabel={t("units.metric")}
                      imperialLabel={t("units.imperial")}
                    />
                  </div>

                  {/* 尺寸输入：3列网格布局 */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="relative">
                      <Input
                        id="length"
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={formData.length}
                        onChange={(e) => updateField("length", e.target.value)}
                        placeholder="0"
                        className={`h-10 text-center ${formErrors.dimensions ? "border-destructive" : ""}`}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-medium">
                        {t("form.length")}
                      </span>
                    </div>
                    <div className="relative">
                      <Input
                        id="width"
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={formData.width}
                        onChange={(e) => updateField("width", e.target.value)}
                        placeholder="0"
                        className={`h-10 text-center ${formErrors.dimensions ? "border-destructive" : ""}`}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-medium">
                        {t("form.width")}
                      </span>
                    </div>
                    <div className="relative">
                      <Input
                        id="height"
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={formData.height}
                        onChange={(e) => updateField("height", e.target.value)}
                        placeholder="0"
                        className={`h-10 text-center ${formErrors.dimensions ? "border-destructive" : ""}`}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-medium">
                        {t("form.height")}
                      </span>
                    </div>
                  </div>
                  {formErrors.dimensions && (
                    <p className="text-sm text-destructive flex items-center gap-1">
                      <AlertCircle className="h-3.5 w-3.5" />
                      {formErrors.dimensions}
                    </p>
                  )}

                  {/* 容积预览 - 紧跟尺寸输入 */}
                  {formData.length && formData.width && formData.height && (
                    <div className="bg-gradient-to-r from-blue-50 via-cyan-50 to-blue-50 dark:from-blue-950/30 dark:via-cyan-950/30 dark:to-blue-950/30 border-2 border-blue-200 dark:border-blue-800 rounded-xl p-3.5 shadow-sm">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                          <Droplets className="h-5 w-5 text-blue-500" />
                        </div>
                        <div className="flex-1 space-y-1">
                          <div className="text-sm">
                            <span className="text-muted-foreground">
                              {t("volume.totalVolume")}:
                            </span>{" "}
                            <span className="font-semibold">
                              {Math.round(volumeResult.totalVolume)}{" "}
                              {volumeUnit}
                            </span>
                          </div>
                          <div className="text-sm">
                            <span className="text-muted-foreground">
                              {t("volume.effectiveVolume")}:
                            </span>{" "}
                            <span className="font-bold text-primary">
                              {Math.round(volumeResult.effectiveVolume)}{" "}
                              {volumeUnit}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 实际水位高度 */}
                  <div className="space-y-2">
                    <Label
                      htmlFor="actual_water_level"
                      className="text-sm font-medium"
                    >
                      {t("form.actualWaterLevel")}
                      <span className="text-muted-foreground text-xs ml-2">
                        {t("form.actualWaterLevelPlaceholder")}
                      </span>
                    </Label>
                    <div className="relative">
                      <Input
                        id="actual_water_level"
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.actual_water_level}
                        onChange={(e) =>
                          updateField("actual_water_level", e.target.value)
                        }
                        placeholder={t("form.actualWaterLevelPlaceholder")}
                        className={`h-10 pr-10 ${formErrors.actual_water_level ? "border-destructive" : ""}`}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                        {dimensionUnit}
                      </span>
                    </div>
                    {formErrors.actual_water_level && (
                      <p className="text-sm text-destructive flex items-center gap-1">
                        <AlertCircle className="h-3.5 w-3.5" />
                        {formErrors.actual_water_level}
                      </p>
                    )}
                  </div>
                </div>

                {/* ========== 水体配置区块 ========== */}
                <div className="space-y-4 pt-5 border-t">
                  {/* 水体类型 - ToggleGroup 风格 */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      {t("waterType.label")}{" "}
                      <span className="text-destructive">*</span>
                    </Label>
                    <div className="grid grid-cols-3 gap-3">
                      <button
                        type="button"
                        onClick={() => updateField("water_type", "freshwater")}
                        className={`flex items-center justify-center gap-2 px-2 py-3.5 rounded-xl border-2 transition-all font-semibold ${
                          formData.water_type === "freshwater"
                            ? "border-primary bg-primary/10 text-primary shadow-sm"
                            : "border-border hover:border-primary/50 hover:bg-muted/50"
                        }`}
                      >
                        <span className="text-lg">🌿</span>
                        <span className="text-sm">
                          {t("waterType.freshwater")}
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => updateField("water_type", "saltwater")}
                        className={`flex items-center justify-center gap-2 px-2 py-3.5 rounded-xl border-2 transition-all font-semibold ${
                          formData.water_type === "saltwater"
                            ? "border-primary bg-primary/10 text-primary shadow-sm"
                            : "border-border hover:border-primary/50 hover:bg-muted/50"
                        }`}
                      >
                        <span className="text-lg">🪸</span>
                        <span className="text-sm">
                          {t("waterType.saltwater")}
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => updateField("water_type", "brackish")}
                        className={`flex items-center justify-center gap-2 px-2 py-3.5 rounded-xl border-2 transition-all font-semibold ${
                          formData.water_type === "brackish"
                            ? "border-primary bg-primary/10 text-primary shadow-sm"
                            : "border-border hover:border-primary/50 hover:bg-muted/50"
                        }`}
                      >
                        <span className="text-lg">🌊</span>
                        <span className="text-sm">
                          {t("waterType.brackish")}
                        </span>
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t(`waterType.${formData.water_type}Description`)}
                    </p>
                  </div>
                </div>

                {/* ========== 设备配置区块 ========== */}
                <div className="space-y-4 pt-5 border-t">
                  {/* 过滤系统 + 位置：2列网格 */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label
                        htmlFor="filter_system"
                        className="text-sm font-medium"
                      >
                        {t("form.filterSystem")}{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <Select
                        value={formData.filter_system}
                        onValueChange={(val) =>
                          updateField("filter_system", val)
                        }
                      >
                        <SelectTrigger className="w-full h-10">
                          <SelectValue placeholder={t("form.filterSystem")} />
                        </SelectTrigger>
                        <SelectContent>
                          {filterSystemOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="location" className="text-sm font-medium">
                        {t("form.location")}{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <Select
                        value={formData.location}
                        onValueChange={(val) => updateField("location", val)}
                      >
                        <SelectTrigger className="w-full h-10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {locationOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* 水泵流量 */}
                  <div className="space-y-2">
                    <Label
                      htmlFor="pump_flow_rate"
                      className="text-sm font-medium"
                    >
                      {t("form.pumpFlowRate")}
                      <span className="text-muted-foreground text-xs ml-2">
                        {t("form.optional") || "选填"}
                      </span>
                    </Label>
                    <div className="relative">
                      <Input
                        id="pump_flow_rate"
                        type="number"
                        step="0.1"
                        min="0"
                        value={formData.pump_flow_rate}
                        onChange={(e) =>
                          updateField("pump_flow_rate", e.target.value)
                        }
                        placeholder={t("form.pumpFlowRatePlaceholder")}
                        className="h-10 pr-14"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                        {flowRateUnit}
                      </span>
                    </div>
                    {/* 循环倍率提示 */}
                    {formData.pump_flow_rate &&
                      formData.length &&
                      formData.width &&
                      formData.height && (
                        <div className="pt-2">
                          <TurnoverRateDisplay
                            rate={volumeResult.turnoverRate}
                            status={volumeResult.turnoverStatus}
                            label={t("turnover.label")}
                            noFlowRateText={t("turnover.noFlowRate")}
                            unitSuffix={t("units.timesPerHour")}
                            lowTip={t("turnover.lowTip")}
                            highTip={t("turnover.highTip")}
                          />
                        </div>
                      )}
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="flex-shrink-0 border-t bg-muted/30 px-6 py-4">
              <div className="flex gap-3 w-full">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  disabled={isSubmitting}
                  className="flex-1 h-10"
                >
                  {t("actions.cancel")}
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 h-10 font-semibold"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {editingTank ? t("actions.save") : t("actions.create")}...
                    </>
                  ) : (
                    <>{editingTank ? t("actions.save") : t("actions.create")}</>
                  )}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* 删除确认对话框 */}
      <AlertDialog
        open={!!deletingTank}
        onOpenChange={() => setDeletingTank(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除鱼缸？</AlertDialogTitle>
            <AlertDialogDescription>
              你确定要删除"{deletingTank?.name}"吗？此操作无法撤销。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTank}
              className="bg-red-600 hover:bg-red-700"
            >
              删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
